import { useState, useEffect, useRef } from 'react';
import type { AgentState } from '../../types/api';
import DataStream from './DataStream';

interface AgentCardProps {
  agent: AgentState;
}

function AgentIcon({ icon, className }: { icon: AgentState['icon']; className?: string }) {
  const cls = className ?? 'w-5 h-5';
  switch (icon) {
    case 'user':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'trophy':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
      );
    case 'target':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case 'brain':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.5 2A5.5 5.5 0 0 0 5 7.5c0 .96.246 1.87.68 2.66A5.502 5.502 0 0 0 4 15c0 2.76 2.04 5.04 4.69 5.42A3 3 0 0 0 12 22a3 3 0 0 0 3.31-1.58C17.96 20.04 20 17.76 20 15a5.5 5.5 0 0 0-1.68-3.96c.44-.75.68-1.62.68-2.54A5.5 5.5 0 0 0 14.5 3 5.5 5.5 0 0 0 12 3.6 5.5 5.5 0 0 0 9.5 2Z" />
          <path d="M12 2v20" />
        </svg>
      );
  }
}

function StatusDot({ status }: { status: AgentState['status'] }) {
  const colors: Record<AgentState['status'], string> = {
    waiting: 'bg-ti-text-dim',
    searching: 'bg-ti-blue',
    found: 'bg-ti-green',
    analyzed: 'bg-ti-gold',
  };

  return (
    <span className="relative inline-flex items-center justify-center">
      <span className={`w-2 h-2 rounded-full ${colors[status]}`} />
      {status === 'searching' && (
        <span className={`absolute w-2 h-2 rounded-full ${colors[status]} animate-pulse-ring`} />
      )}
    </span>
  );
}

function statusLabel(status: AgentState['status']): string {
  switch (status) {
    case 'waiting': return 'Standby';
    case 'searching': return 'Searching...';
    case 'found': return 'Data Retrieved';
    case 'analyzed': return 'Analysis Complete';
  }
}

const SYNTHESIS_SECONDS = 20;

function SynthesisCountdown() {
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setElapsed((prev) => {
        if (prev >= SYNTHESIS_SECONDS) {
          if (timerRef.current) clearInterval(timerRef.current);
          return SYNTHESIS_SECONDS;
        }
        return prev + 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const remaining = Math.max(0, SYNTHESIS_SECONDS - elapsed);
  const progress = (elapsed / SYNTHESIS_SECONDS) * 100;
  const isDone = remaining === 0;

  return (
    <div className="mb-3 p-4 bg-ti-blue/5 border border-ti-blue/20 rounded-lg space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-ti-blue rounded-full animate-bounce" />
          <div className="w-2 h-2 bg-ti-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
          <div className="w-2 h-2 bg-ti-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>
        <div className="text-xs text-ti-blue">
          <p className="font-medium">
            {isDone ? 'Compiling intelligence report...' : 'TalentAgent analyzing candidate fit...'}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="h-1.5 bg-ti-border rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-ti-blue/70 to-ti-blue rounded-full"
            style={{ width: `${Math.min(progress, 100)}%`, transition: 'width 1s linear' }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-ti-blue/60 font-mono">
            {isDone ? 'Finalizing...' : `${remaining}s remaining`}
          </span>
          <span className="text-[10px] text-ti-blue/60 font-mono">
            {Math.round(progress)}%
          </span>
        </div>
      </div>
    </div>
  );
}

export default function AgentCard({ agent }: AgentCardProps) {
  const borderColor: Record<AgentState['status'], string> = {
    waiting: 'border-ti-border',
    searching: 'border-ti-cyan/60',
    found: 'border-ti-green/40',
    analyzed: 'border-ti-gold/60',
  };

  const cardOpacity = agent.status === 'waiting' ? 'opacity-50' : 'opacity-100';

  const responseTime =
    agent.startTime && agent.endTime
      ? `${agent.endTime - agent.startTime}ms`
      : null;

  return (
    <div
      className={`relative bg-ti-surface rounded-xl p-5 border transition-all duration-500 ${borderColor[agent.status]} ${cardOpacity}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg ${
            agent.status === 'searching'
              ? 'bg-ti-cyan/10 text-ti-cyan'
              : agent.status === 'found'
                ? 'bg-ti-green/10 text-ti-green'
                : agent.status === 'analyzed'
                  ? 'bg-ti-gold/10 text-ti-gold'
                  : 'bg-ti-border text-ti-text-dim'
          }`}>
            <AgentIcon icon={agent.icon} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ti-text">{agent.name}</h3>
            <p className="text-xs text-ti-text-dim">{agent.description}</p>
          </div>
        </div>
        <StatusDot status={agent.status} />
      </div>

      {/* Status label */}
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-medium ${
          agent.status === 'searching'
            ? 'text-ti-cyan'
            : agent.status === 'found'
              ? 'text-ti-green'
              : agent.status === 'analyzed'
                ? 'text-ti-gold'
                : 'text-ti-text-dim'
        }`}>
          {statusLabel(agent.status)}
        </span>
        {responseTime && (
          <span className="text-xs font-mono text-ti-text-dim">
            {responseTime}
          </span>
        )}
      </div>

      {/* Special message for Intelligence Synthesis agent */}
      {agent.id === 'synthesis' && agent.status === 'searching' && (
        <SynthesisCountdown />
      )}

      {/* Endpoint list (during searching) */}
      {(agent.status === 'searching' || agent.status === 'found' || agent.status === 'analyzed') && agent.endpoints.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {agent.endpoints.map((endpoint, i) => {
            const snippetIndex = agent.dataSnippets.findIndex((_, si) => si === i);
            const hasData = snippetIndex !== -1 && snippetIndex < agent.dataSnippets.length;

            return (
              <div
                key={endpoint}
                className="flex items-center gap-2 text-xs font-mono"
                style={{
                  animationDelay: `${i * 300}ms`,
                  animation: agent.status === 'searching' && !hasData
                    ? 'pulse 1.5s ease-in-out infinite'
                    : 'none',
                }}
              >
                <span className="text-ti-text-dim">GET</span>
                <span className={hasData ? 'text-ti-green' : 'text-ti-cyan'}>
                  {endpoint}
                </span>
                {agent.status === 'searching' && !hasData && (
                  <span className="inline-block w-3 h-3">
                    <svg className="animate-spin w-3 h-3 text-ti-cyan/60" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
                    </svg>
                  </span>
                )}
                {hasData && (
                  <svg className="w-3 h-3 text-ti-green" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L7 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Data stream */}
      {agent.dataSnippets.length > 0 && (
        <DataStream
          snippets={agent.dataSnippets}
          isActive={agent.status === 'searching'}
        />
      )}

      {/* Summary when found */}
      {(agent.status === 'found' || agent.status === 'analyzed') && agent.endpoints.length > 0 && (
        <div className="mt-2 text-xs text-ti-text-muted">
          Retrieved {agent.dataSnippets.length} data points from {agent.endpoints.length} endpoints
        </div>
      )}
      {agent.status === 'analyzed' && agent.endpoints.length === 0 && (
        <div className="mt-2 text-xs text-ti-text-muted">
          Intelligence brief compiled from {agent.dataSnippets.length > 0 ? 'all gathered data' : 'candidate profile'}
        </div>
      )}

      {/* Analyzed badge */}
      {agent.status === 'analyzed' && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-ti-gold/10 border border-ti-gold/30 rounded-full">
          <svg className="w-3 h-3 text-ti-gold" viewBox="0 0 16 16" fill="none">
            <path d="M3 8L7 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-xs font-medium text-ti-gold">Analysis Complete</span>
        </div>
      )}
    </div>
  );
}
